import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Printer, ArrowLeft, Sparkles, ArrowRight, MessageCircle, Mail,
  Share2, Copy, Check, Smartphone, ShieldCheck, Zap, BadgeCheck, Coins, Bell,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/connect-otp';
const SHARE_MESSAGE = (
  `Just saw this on DocSite — when a new patient reaches you on your Connect link, ` +
  `their phone number is now verified before they can message you. Real patients only, ` +
  `no fake leads, no junk entries.\n\n` +
  `Patients who already use the DocSite app get a one-tap verification on their phone, ` +
  `so it stays effortless for repeat patients.\n\n` +
  `Free Plan: 2 verifications/day, 10/month. Premium / TurboCharge: unlimited.\n\n` +
  `1-pager: ${SHARE_URL}`
);

/**
 * Public 1-pager announcing the patient-verification launch on the Connect page.
 * Audience: existing DocSite doctors (via dashboard banner) and non-members
 * (forwarded link / WhatsApp). Copy is written in doctor-first language —
 * no slugs, no rate-limit jargon, no infrastructure references.
 */
export default function ConnectOtpLaunch() {
  const [copied, setCopied] = React.useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const doPrint = () => window.print();

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent('DocSite — every new patient is now verified before they reach you')}&body=${encodeURIComponent(SHARE_MESSAGE)}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — verified patients only', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* user cancelled */ }
    } else { copyLink(); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Every new patient is now verified before they reach you</title>
        <meta name="description" content="DocSite now verifies every new patient who reaches you on your Connect link, before their message lands in your inbox. Patients on the DocSite app get a one-tap verification. No fake leads, no junk entries, no extra work for you." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="patient verification for doctors, verified patient leads, OTP for doctor websites, DocSite Connect link, real patient inquiries India, no spam patient leads" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="DocSite — Every new patient is now verified before they reach you" />
        <meta property="og:description" content="No fake leads, no junk entries. Real patients only, in one tap." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="article:published_time" content="2026-02-06" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DocSite — Every new patient is now verified before they reach you" />
        <meta name="twitter:description" content="No fake leads, no junk entries. Real patients only, in one tap." />
        <link rel="canonical" href={SHARE_URL} />
        {/* NewsArticle JSON-LD — flags this as a dated launch announcement so
            Google can surface it in News, Discover, and rich-result snippets. */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: 'Every new patient is now verified before they reach you',
          description: 'When a new patient reaches you on your DocSite Connect link, their phone number is verified before their message lands with you. No fake leads, no junk entries.',
          datePublished: '2026-02-06',
          dateModified: '2026-02-06',
          author: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care' },
          publisher: {
            '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care',
            logo: { '@type': 'ImageObject', url: 'https://docsite.care/docsite-logo.png' },
          },
          mainEntityOfPage: SHARE_URL,
          keywords: 'patient verification, doctor lead quality, DocSite Connect link, OTP for doctors, verified patient leads',
          articleSection: 'Product Launches',
          inLanguage: 'en-IN',
        })}</script>
        {/* BreadcrumbList — home → What's New → this launch */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'DocSite', item: 'https://docsite.care' },
            { '@type': 'ListItem', position: 2, name: "What's New", item: 'https://docsite.care/whats-new' },
            { '@type': 'ListItem', position: 3, name: 'Verified Patient Inquiries', item: SHARE_URL },
          ],
        })}</script>
      </Helmet>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 14mm 12mm; }
          html, body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { max-width: 100% !important; padding: 0 !important; box-shadow: none !important; }
          .print-row { break-inside: avoid; }
          .print-section { break-inside: avoid-page; }
        }
      `}</style>

      {/* Screen-only sticky action bar */}
      <div className="no-print sticky top-0 z-40 bg-white border-b border-slate-200 py-3 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="launch-back">
            <ArrowLeft size={14} /> Back to docsite.care
          </Link>
          <button onClick={doPrint} data-testid="launch-print-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="print-page max-w-4xl mx-auto bg-white p-8 md:p-12 my-6 shadow-sm print:my-0 print:shadow-none print:p-0" data-testid="connect-otp-launch-page">

        {/* Header */}
        <header className="flex items-start justify-between mb-8 pb-6 border-b-2 border-slate-900 print-section">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-sky-600 flex items-center gap-1.5">
              <Sparkles size={11} /> New on DocSite · Just Launched
            </p>
            <h1 className="font-medium text-slate-900 mt-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 3.2vw, 3.75rem)', lineHeight: 1.1 }}>
              Every new patient<br />
              <span className="text-sky-600">is now verified</span>
              <br />before they reach you.
            </h1>
            <p className="text-sm text-slate-500 mt-3 max-w-xl leading-relaxed">
              When a new patient reaches you on your Connect link, their phone number is verified
              before their details land with you. Patients you've already seen, and patients on
              the DocSite app, glide through with one tap.
            </p>
          </div>
          <div className="text-right text-xs text-slate-500 shrink-0">
            <p className="font-semibold text-slate-900">docsite.care</p>
            <p className="mt-1">Launched: {todayIso}</p>
          </div>
        </header>

        {/* Why this matters — 3 doctor outcomes */}
        <section className="print-section mb-10">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">What it does for your practice</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 print-row" data-testid="launch-why-trust">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center mb-2"><BadgeCheck size={16} className="text-emerald-600" /></div>
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Real patients, real numbers</p>
              <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">Every name in your patient directory now belongs to a verified phone you can call back. No more wondering whether a number is real before you pick up the phone.</p>
            </div>
            <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 print-row" data-testid="launch-why-zero-spam">
              <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center mb-2"><ShieldCheck size={16} className="text-sky-600" /></div>
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>No fake leads, no junk entries</p>
              <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">Auto-fill scripts, prank entries, and competitor mischief are filtered out before the form opens. You only see patients who actually want to reach you.</p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 print-row" data-testid="launch-why-frictionless">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center mb-2"><Zap size={16} className="text-amber-600" /></div>
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Effortless for your regulars</p>
              <p className="text-[11px] text-slate-600 mt-1.5 leading-relaxed">Patients you've already seen are recognised instantly — no verification step. Patients with the DocSite app verify in one tap. Only true first-time visitors are asked to enter a code.</p>
            </div>
          </div>
        </section>

        {/* What the patient sees — 3 short steps */}
        <section className="print-section mb-10 rounded-2xl border border-slate-200 bg-slate-50/50 p-5">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4 flex items-center gap-1.5"><Smartphone size={11} /> What your patient sees</h2>
          <ol className="space-y-3">
            <li className="flex gap-3 print-row">
              <span className="w-7 h-7 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>01</span>
              <div>
                <p className="text-sm font-semibold text-slate-900">They open your Connect link and type their phone number</p>
                <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">The rest of the form stays hidden until DocSite recognises them — no overwhelming first impression.</p>
              </div>
            </li>
            <li className="flex gap-3 print-row">
              <span className="w-7 h-7 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>02</span>
              <div>
                <p className="text-sm font-semibold text-slate-900">DocSite recognises them in the right way</p>
                <ul className="text-[11px] text-slate-600 mt-1 space-y-1 leading-relaxed">
                  <li>• <b>Already your patient</b>, or already on DocSite as a patient — recognised instantly, no extra step.</li>
                  <li>• <b>Has the DocSite app</b> — gets a one-tap verification on their phone. Quietly, no SMS.</li>
                  <li>• <b>Brand-new to DocSite</b> — gets a quick code via SMS to confirm their number.</li>
                </ul>
              </div>
            </li>
            <li className="flex gap-3 print-row">
              <span className="w-7 h-7 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>03</span>
              <div>
                <p className="text-sm font-semibold text-slate-900">The form opens. They share their name, visit details, and a quick "how the consult went" note.</p>
                <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">The thank-you screen now also offers them a one-tap <b>"Save Dr. ___ to my phone"</b> — they create a free DocSite patient account, and your clinic shows up under their saved doctors forever.</p>
              </div>
            </li>
          </ol>
        </section>

        {/* What's included on each plan */}
        <section className="print-section mb-10">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">How many new-patient verifications are included</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-amber-300 p-4 bg-amber-50/40 print-row" data-testid="launch-plan-free">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600">Gold (Free)</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>2 / day</p>
              <p className="text-[11px] text-amber-900 font-semibold">10 / month</p>
              <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">Comfortable for a quiet clinic. If a real patient ever hits the limit, the form gracefully lets them continue — they're never turned away.</p>
            </div>
            <div className="rounded-xl border border-sky-300 p-4 bg-sky-50/40 print-row" data-testid="launch-plan-premium">
              <p className="text-[10px] uppercase tracking-wider font-bold text-sky-600">Coffee Premium · Rs. 299 / mo</p>
              <p className="text-2xl font-bold text-sky-800 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Unlimited</p>
              <p className="text-[11px] text-sky-900 font-semibold">new-patient verifications</p>
              <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">Plus: per-procedure pricing, embedded clinic map, in-booking patient chat, reliability scoring.</p>
            </div>
            <div className="rounded-xl border border-emerald-300 p-4 bg-emerald-50/40 print-row" data-testid="launch-plan-turbo">
              <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-600">TurboCharge · Rs. 999 / mo</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Unlimited</p>
              <p className="text-[11px] text-emerald-900 font-semibold">+ Auto-SEO + AI assistance</p>
              <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">Plus: monthly AI assistance, printable Clinic QR Kit, organic-search landing pages, Visibility Accelerator checklist.</p>
            </div>
          </div>
        </section>

        {/* Bottom-line equation — outcome-focused */}
        <section className="print-section mb-10 rounded-xl border-2 border-sky-600 bg-gradient-to-br from-sky-50 to-emerald-50 p-5" data-testid="launch-equation">
          <p className="text-[10px] font-bold text-sky-700 uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5">
            <Coins size={11} /> What it adds up to
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 font-bold text-sky-900 py-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="text-base md:text-xl">Real Patients</span>
            <span className="text-lg md:text-2xl text-emerald-600" aria-hidden="true">+</span>
            <span className="text-base md:text-xl">Zero Junk Entries</span>
            <span className="text-lg md:text-2xl text-emerald-600" aria-hidden="true">+</span>
            <span className="text-base md:text-xl">Effortless for Your Regulars</span>
            <span className="text-lg md:text-2xl text-amber-600" aria-hidden="true">=</span>
            <span className="text-base md:text-xl text-emerald-700">A Practice That Compounds</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 italic leading-relaxed text-center">Every patient who reaches you is a real patient — and they stay yours. Not a re-sellable lead in someone else's directory.</p>
        </section>

        {/* Share — screen-only */}
        <section className="no-print mb-8 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50 p-5" data-testid="launch-share">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 mb-2 flex items-center gap-1.5"><Bell size={11} /> Tell a fellow doctor about this</p>
          <p className="text-sm text-slate-700 mb-4 leading-relaxed">
            One tap and the message goes out from your own WhatsApp or email. We never message your contacts on your behalf.
          </p>
          <div className="flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
               data-testid="launch-share-whatsapp">
              <MessageCircle size={13} /> WhatsApp a colleague
            </a>
            <a href={mailHref}
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors"
               data-testid="launch-share-email">
              <Mail size={13} /> Email
            </a>
            <button onClick={nativeShare}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
                    data-testid="launch-share-native">
              <Share2 size={13} /> More…
            </button>
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300 transition-colors"
                    data-testid="launch-share-copy">
              {copied ? <><Check size={13} className="text-emerald-600" /> Link copied</> : <><Copy size={13} /> Copy link</>}
            </button>
          </div>
        </section>

        {/* CTAs — screen-only */}
        <section className="no-print flex flex-wrap gap-3">
          <Link to="/register?as=doctor&from=launch_connect_otp" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800" data-testid="launch-cta-register">
            Start free in 3 clicks <ArrowRight size={14} />
          </Link>
          <Link to="/dashboard" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700" data-testid="launch-cta-dashboard">
            Open my dashboard
          </Link>
          <Link to="/brochure" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300" data-testid="launch-cta-brochure">
            See everything DocSite includes
          </Link>
          <button onClick={doPrint} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600" data-testid="launch-cta-print">
            <Printer size={14} /> Print this 1-pager
          </button>
        </section>

        {/* Footer — doctor-friendly. No infrastructure jargon. */}
        <footer className="mt-10 pt-6 border-t border-slate-200 text-[10px] text-slate-400 leading-relaxed">
          <p className="flex items-center gap-1.5 mb-1"><ShieldCheck size={10} /> Patient phone numbers stay private to your practice. DocSite never resells, never directories, never auctions your patient list.</p>
          <p>Plan limits and pricing accurate as of {todayIso} and may evolve as the platform grows. No commissions on consults. Cancel anytime.</p>
        </footer>
      </div>
    </div>
  );
}
